import { apiFetch, apiUrl, wsUrl } from "@/lib/api";
import type {
  Book,
  BookDetail,
  BookProposal,
  Page,
  Spine,
  Block,
} from "@/lib/book-types";

const BASE = "/api/v1/book";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(`${BASE}${path}`), {
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let detail: string;
    try {
      const data = await res.json();
      detail = (data && (data.detail || data.message)) || res.statusText;
    } catch {
      detail = res.statusText;
    }
    throw new Error(`book api ${path} → ${res.status}: ${detail}`);
  }
  return (await res.json()) as T;
}

export interface CreateBookPayload {
  user_intent: string;
  chat_session_id?: string;
  chat_selections?: Array<{ session_id: string; message_ids: number[] }>;
  notebook_refs?: Array<Record<string, unknown>>;
  knowledge_bases?: string[];
  question_categories?: number[];
  question_entries?: number[];
  language?: string;
}

export const bookApi = {
  list: () => request<{ books: Book[] }>("/books"),
  get: (book_id: string) =>
    request<BookDetail>(`/books/${encodeURIComponent(book_id)}`),
  delete: (book_id: string) =>
    request<{ deleted: boolean; book_id: string }>(
      `/books/${encodeURIComponent(book_id)}`,
      { method: "DELETE" },
    ),
  getSpine: (book_id: string) =>
    request<{ spine: Spine }>(`/books/${encodeURIComponent(book_id)}/spine`),
  getPage: (book_id: string, page_id: string) =>
    request<{ page: Page }>(
      `/books/${encodeURIComponent(book_id)}/pages/${encodeURIComponent(page_id)}`,
    ),
  // Long ops go over the WS — see BookSocket below.
  create: (payload: CreateBookPayload) =>
    bookSocket.call<{ book: Book; proposal: BookProposal }>("create", {
      ...payload,
    }),
  confirmProposal: (book_id: string, proposal?: BookProposal) =>
    bookSocket.call<{ book: Book; spine: Spine }>("confirm_proposal", {
      book_id,
      proposal: proposal ?? null,
    }),
  confirmSpine: (book_id: string, spine?: Spine, auto_compile = true) =>
    bookSocket.call<{ pages: Page[] }>("confirm_spine", {
      book_id,
      spine: spine ?? null,
      auto_compile,
    }),
  compilePage: (book_id: string, page_id: string, force = false) =>
    bookSocket.call<{ page: Page }>("compile_page", { book_id, page_id, force }),
  regenerateBlock: (
    book_id: string,
    page_id: string,
    block_id: string,
    params_override?: Record<string, unknown>,
  ) =>
    bookSocket.call<{ block: Block | null }>("regenerate_block", {
      book_id,
      page_id,
      block_id,
      params_override: params_override ?? null,
    }),

  insertBlock: (params: {
    book_id: string;
    page_id: string;
    block_type: string;
    params?: Record<string, unknown>;
    position?: number;
    compile_now?: boolean;
  }) =>
    request<{ block: Block }>("/books/insert-block", {
      method: "POST",
      body: JSON.stringify({
        compile_now: true,
        ...params,
      }),
    }),

  deleteBlock: (book_id: string, page_id: string, block_id: string) =>
    request<{ ok: boolean }>("/books/delete-block", {
      method: "POST",
      body: JSON.stringify({ book_id, page_id, block_id }),
    }),

  moveBlock: (
    book_id: string,
    page_id: string,
    block_id: string,
    new_position: number,
  ) =>
    request<{ ok: boolean }>("/books/move-block", {
      method: "POST",
      body: JSON.stringify({ book_id, page_id, block_id, new_position }),
    }),

  changeBlockType: (params: {
    book_id: string;
    page_id: string;
    block_id: string;
    new_type: string;
    params_override?: Record<string, unknown>;
  }) =>
    request<{ block: Block }>("/books/change-block-type", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  deepDive: (params: {
    book_id: string;
    parent_page_id: string;
    topic: string;
    block_id?: string;
    content_type?: string;
  }) =>
    bookSocket.call<{ page: Page }>("deep_dive", {
      content_type: "concept",
      ...params,
    }),

  recordPageVisit: (book_id: string, page_id: string) =>
    request<{ progress: Record<string, unknown> }>("/books/progress", {
      method: "POST",
      body: JSON.stringify({ book_id, page_id }),
    }),

  recordQuizAttempt: (params: {
    book_id: string;
    page_id: string;
    block_id: string;
    question_id?: string;
    user_answer?: string;
    is_correct: boolean;
  }) =>
    request<{ progress: Record<string, unknown> }>("/books/quiz-attempt", {
      method: "POST",
      body: JSON.stringify(params),
    }),

  supplement: (book_id: string, page_id: string, topic: string) =>
    request<{ block: Block }>("/books/supplement", {
      method: "POST",
      body: JSON.stringify({ book_id, page_id, topic }),
    }),

  setPageChatSession: (book_id: string, page_id: string, session_id: string) =>
    request<{ book: Book }>("/books/page-chat-session", {
      method: "POST",
      body: JSON.stringify({ book_id, page_id, session_id }),
    }),

  rebuild: (book_id: string, auto_compile = true) =>
    bookSocket.call<{ pages: Page[] }>("rebuild", { book_id, auto_compile }),

  health: (book_id: string) =>
    request<{
      kb_drift: {
        book_id: string;
        has_drift: boolean;
        new_kbs?: string[];
        removed_kbs?: string[];
        changed_kbs?: string[];
        stale_page_ids?: string[];
      };
      log_health: {
        book_id: string;
        total_entries: number;
        error_entries: number;
        block_failures: number;
        last_compile_at?: string;
        last_error_at?: string;
        repeated_failures?: { signature: string; count: number }[];
      };
    }>(`/books/${encodeURIComponent(book_id)}/health`),

  refreshFingerprints: (book_id: string) =>
    request<{
      book_id: string;
      kb_fingerprints: Record<string, string>;
      stale_page_ids: string[];
    }>(`/books/${encodeURIComponent(book_id)}/refresh-fingerprints`, {
      method: "POST",
    }),
};

export interface LegacyChatSession {
  session_id: string;
  messages?: Array<{ role: string; content: string }>;
}

export async function getLegacyChatSession(
  session_id: string,
): Promise<LegacyChatSession | null> {
  const res = await apiFetch(
    apiUrl(`/api/v1/chat/sessions/${encodeURIComponent(session_id)}`),
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`chat session ${session_id} → ${res.status}`);
  return (await res.json()) as LegacyChatSession;
}

// ── WebSocket RPC ────────────────────────────────────────────────────
//
// The long book operations (ideation, spine synthesis, page compilation,
// rebuild, deep dive) run for tens of seconds to minutes — past the idle
// ceiling of every HTTP hop in front of us (the Next dev proxy caps at 30s by
// default, and tunnels impose their own). They ride this socket instead.
//
// Each call carries a `request_id` that the backend echoes on its streamed
// events and its terminating `<type>_result` / `error` frame. If the socket
// drops mid-operation the call is re-sent with the *same* id on reconnect: the
// backend's in-flight guard joins the still-running task rather than starting
// a second one, so the result survives a dropped connection.

export type BookWsEvent = { type: string; [key: string]: unknown };

type Pending = {
  type: string;
  payload: Record<string, unknown>;
  resolve: (value: never) => void;
  reject: (error: Error) => void;
  deadline: ReturnType<typeof setTimeout>;
};

// Give up reconnecting after this many consecutive failures (~30s of backoff)
// and fail the outstanding calls: a caller that never settles leaves the UI
// spinning forever, which is worse than a visible error.
const MAX_RETRIES = 6;
// Backstop for a socket that stays open but never answers.
const CALL_DEADLINE_MS = 15 * 60 * 1000;

class BookSocket {
  private socket: WebSocket | null = null;
  private pending = new Map<string, Pending>();
  private listeners = new Set<(event: BookWsEvent) => void>();
  private seq = 0;
  private retry = 0;

  subscribe(onEvent: (event: BookWsEvent) => void): () => void {
    this.listeners.add(onEvent);
    this.connect();
    return () => {
      this.listeners.delete(onEvent);
    };
  }

  call<T>(type: string, payload: Record<string, unknown> = {}): Promise<T> {
    const id = `${type}-${++this.seq}-${Math.random().toString(36).slice(2, 8)}`;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        type,
        payload,
        resolve: resolve as (value: never) => void,
        reject,
        deadline: setTimeout(
          () => this.fail(id, new Error(`book ws ${type} → timed out`)),
          CALL_DEADLINE_MS,
        ),
      });
      this.send(id);
    });
  }

  private fail(id: string, error: Error) {
    const call = this.pending.get(id);
    if (!call) return;
    this.pending.delete(id);
    clearTimeout(call.deadline);
    call.reject(error);
  }

  private failAll(error: Error) {
    for (const id of Array.from(this.pending.keys())) this.fail(id, error);
  }

  private send(id: string) {
    const call = this.pending.get(id);
    if (!call) return;
    const socket = this.connect();
    // Not open yet → onopen replays every pending call.
    if (socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(
        JSON.stringify({ type: call.type, request_id: id, ...call.payload }),
      );
    } catch {
      // Reconnect will replay it.
    }
  }

  private connect(): WebSocket {
    const existing = this.socket;
    if (
      existing &&
      (existing.readyState === WebSocket.CONNECTING ||
        existing.readyState === WebSocket.OPEN)
    ) {
      return existing;
    }
    const socket = new WebSocket(wsUrl(`${BASE}/ws`));
    this.socket = socket;
    socket.onopen = () => {
      this.retry = 0;
      for (const id of Array.from(this.pending.keys())) this.send(id);
    };
    socket.onmessage = (event) => this.onFrame(String(event.data));
    // Some runtimes fire only one of error/close on a refused connection, so
    // both funnel into the same once-guarded teardown.
    let down = false;
    const onDown = () => {
      if (down) return;
      down = true;
      try {
        socket.close();
      } catch {
        // ignore
      }
      if (this.socket === socket) this.socket = null;
      this.reconnect();
    };
    socket.onerror = onDown;
    socket.onclose = onDown;
    return socket;
  }

  private reconnect() {
    if (this.pending.size === 0 && this.listeners.size === 0) return;
    if (this.retry >= MAX_RETRIES) {
      this.failAll(new Error("book ws → connection lost"));
      if (this.listeners.size === 0) return;
    }
    const delay = Math.min(500 * 2 ** this.retry++, 15000);
    setTimeout(() => {
      if (this.pending.size === 0 && this.listeners.size === 0) return;
      this.connect();
    }, delay);
  }

  private onFrame(raw: string) {
    let frame: BookWsEvent;
    try {
      frame = JSON.parse(raw);
    } catch {
      return; // malformed
    }
    const id =
      typeof frame.request_id === "string" ? (frame.request_id as string) : "";
    const call = id ? this.pending.get(id) : undefined;
    const type = String(frame.type || "");
    if (call && (type === "error" || type === `${call.type}_result`)) {
      if (type === "error") {
        this.fail(
          id,
          new Error(`book ws ${call.type} → ${String(frame.content ?? "failed")}`),
        );
      } else {
        this.pending.delete(id);
        clearTimeout(call.deadline);
        call.resolve(frame as never);
      }
      return;
    }
    for (const listener of Array.from(this.listeners)) listener(frame);
  }
}

const bookSocket = new BookSocket();

export function openBookSocket(
  onEvent: (event: BookWsEvent) => void,
): { close: () => void } {
  const unsubscribe = bookSocket.subscribe(onEvent);
  return { close: unsubscribe };
}
